import "./facets";
import "./filter-drawer";
import trackGridImpressions from "./view-item-list";

const TRUNCATE_CLASS = "main-collection__product-grid--truncate";

class MainCollection extends HTMLElement {
  constructor() {
    super();
    // Cards fetched ahead but not yet revealed.
    this.buffer = [];
    this.loading = false;
    // Bumped on every (re)sync so in-flight fetches from a superseded result
    // set (e.g. rapid facet changes) detect they're stale and bail.
    this.renderToken = 0;
    this.handleClick = this.handleClick.bind(this);
    this.handleFacetsRendered = this.handleFacetsRendered.bind(this);
  }

  connectedCallback() {
    this.productGrid = this.querySelector("[data-product-grid]");

    // Group size for each reveal.
    this.pageSize = parseInt(this.dataset.pageSize, 10) || 1;

    this.syncFromDom(this.querySelector("[data-load-more]"));
    this.normalizeFirstPage();

    this.addEventListener("click", this.handleClick);
    document.addEventListener("facets:rendered", this.handleFacetsRendered);

    // Elevar picks up product cards' impressions itself, but not style cards'.
    if (this.classList.contains("main-collection--styles")) {
      this.stopTrackingImpressions = trackGridImpressions(this.productGrid);
    }
  }

  disconnectedCallback() {
    document.removeEventListener("facets:rendered", this.handleFacetsRendered);
    this.stopTrackingImpressions?.();
  }

  get shownCount() {
    return this.productGrid.querySelectorAll("[data-product-card]").length;
  }

  progressWrapper() {
    return this.querySelector(".main-collection__load-more-progress-wrapper");
  }

  // (Re)derive paging state from freshly rendered markup, discarding cards
  // buffered for a previous result set.
  syncFromDom(loadMore) {
    this.buffer = [];
    this.renderToken += 1;
    // The server count already covers the first page's extra cards; this
    // accumulates those of each page fetched after it.
    this.extraCards = 0;

    // A hidden button means there's no next page — it's only there to reveal
    // first-page overflow.
    if (loadMore && !loadMore.hidden) {
      this.nextUrl = loadMore.href;
      this.pagesLoaded = parseInt(loadMore.dataset.currentPage, 10) || 1;
    } else {
      this.nextUrl = null;
    }
  }

  // Constrain the grid to exactly `pageSize` cards: buffer first-page overflow,
  // or load further pages to top up a short page (rare — skipped products).
  async normalizeFirstPage() {
    const token = this.renderToken;
    const cards = [...this.productGrid.querySelectorAll("[data-product-card]")];

    if (cards.length > this.pageSize) {
      const overflow = cards.slice(this.pageSize);
      overflow.forEach((card) => card.remove());
      this.buffer.unshift(...overflow);
    } else if (cards.length < this.pageSize && this.nextUrl) {
      this.loading = true;
      try {
        const needed = this.pageSize - cards.length;
        await this.fillBuffer(needed, token);
        if (token !== this.renderToken) return;
        const group = this.buffer.splice(0, needed);
        if (group.length) this.productGrid.append(...group);
      } finally {
        this.loading = false;
      }
    }

    // The truncate cap is only for the initial paint; revealed groups must
    // render in full.
    this.productGrid.classList.remove(TRUNCATE_CLASS);
    this.updateProgress();
    this.updateLoadMoreButton();
  }

  handleFacetsRendered() {
    this.syncFromDom(this.querySelector("[data-load-more]"));
    this.normalizeFirstPage();
  }

  async handleClick(e) {
    const loadMore = e.target.closest("[data-load-more]");
    if (!loadMore) return;

    e.preventDefault();
    if (this.loading) return;

    await this.revealNextGroup(loadMore);
  }

  async revealNextGroup(loadMore) {
    const token = this.renderToken;
    this.loading = true;
    loadMore.setAttribute("aria-busy", "true");

    try {
      await this.fillBuffer(this.pageSize, token);
      if (token !== this.renderToken) return;

      const group = this.buffer.splice(0, this.pageSize);
      if (group.length) {
        this.productGrid.append(...group);
        group[0].querySelector("a")?.focus();
      }

      this.updateProgress();
      this.updateLoadMoreButton();
    } finally {
      loadMore.removeAttribute("aria-busy");
      this.loading = false;
    }
  }

  // Fetch pages until `count` cards are buffered or pages run out.
  async fillBuffer(count, token) {
    while (this.buffer.length < count && this.nextUrl) {
      await this.fetchNextPage(token);
      if (token !== this.renderToken) return;
    }
  }

  async fetchNextPage(token) {
    const url = this.nextUrl;
    if (!url) return;

    // Clear up front so a failed fetch can be retried via the restored url.
    this.nextUrl = null;

    try {
      const response = await fetch(url);
      const text = await response.text();
      if (token !== this.renderToken) return;

      const doc = new DOMParser().parseFromString(text, "text/html");
      this.buffer.push(...doc.querySelectorAll("[data-product-card]"));
      this.extraCards +=
        parseInt(
          doc.querySelector("[data-extra-cards]")?.dataset.extraCards,
          10,
        ) || 0;

      const newLoadMore = doc.querySelector("[data-load-more]");
      if (newLoadMore && !newLoadMore.hidden) {
        this.nextUrl = newLoadMore.href;
        this.pagesLoaded =
          parseInt(newLoadMore.dataset.currentPage, 10) || this.pagesLoaded + 1;
      } else {
        this.nextUrl = null;
      }
    } catch (error) {
      if (token === this.renderToken) this.nextUrl = url;
      throw error;
    }
  }

  updateLoadMoreButton() {
    const loadMore = this.querySelector("[data-load-more]");
    if (!loadMore) return;

    if (!this.buffer.length && !this.nextUrl) {
      // Everything is shown — drop the button but leave the (now full) bar.
      loadMore.remove();
      return;
    }

    loadMore.hidden = false;
    if (this.nextUrl) {
      // Keep href current for the no-JS / middle-click fallback.
      loadMore.href = this.nextUrl;
      loadMore.setAttribute("data-current-page", this.pagesLoaded);
    }
  }

  updateProgress() {
    const wrapper = this.progressWrapper();
    if (!wrapper) return;

    const shown = this.shownCount;
    // Approximate: pages not yet fetched may still hold extra cards.
    const cardCount = parseInt(wrapper.dataset.cardCount, 10);
    const total = Number.isNaN(cardCount) ? shown : cardCount + this.extraCards;

    const showing = wrapper.querySelector("[data-showing]");
    if (showing && wrapper.dataset.showingTemplate) {
      showing.textContent = this.fillTemplate(
        wrapper.dataset.showingTemplate,
        shown,
        total,
      );
    }

    const accessible = wrapper.querySelector("[data-showing-accessible]");
    if (accessible && wrapper.dataset.showingAccessibleTemplate) {
      accessible.textContent = this.fillTemplate(
        wrapper.dataset.showingAccessibleTemplate,
        shown,
        total,
      );
    }

    const bar = wrapper.querySelector("[data-progress-bar]");
    if (bar) {
      const fraction = total ? shown / total : 1;
      bar.style.setProperty("--progress", Math.min(fraction, 1));
    }

    const productCount = this.querySelector("[data-product-count]");
    if (this.extraCards && productCount?.dataset.template) {
      productCount.textContent = productCount.dataset.template.replace(
        "[count]",
        total,
      );
    }
  }

  fillTemplate(template, end, total) {
    return template
      .replace("[start]", "1")
      .replace("[end]", end)
      .replace("[total]", total);
  }
}

if (!customElements.get("main-collection")) {
  customElements.define("main-collection", MainCollection);
}
